import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

/**
 * Async Thunks for EmailTemplate API Calls
 */

// Fetch email templates with filters and pagination
export const getEmailTemplates = createAsyncThunk(
  'emailTemplates/getEmailTemplates',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/email-templates', { params });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Fetch single email template by ID
export const getEmailTemplateById = createAsyncThunk(
  'emailTemplates/getEmailTemplateById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/email-templates/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Create new email template
export const createEmailTemplate = createAsyncThunk(
  'emailTemplates/createEmailTemplate',
  async (templateData, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/email-templates', templateData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Update email template
export const updateEmailTemplate = createAsyncThunk(
  'emailTemplates/updateEmailTemplate',
  async ({ id, templateData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/email-templates/${id}`, templateData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Delete email template (soft delete)
export const deleteEmailTemplate = createAsyncThunk(
  'emailTemplates/deleteEmailTemplate',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/admin/email-templates/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Preview email template with variables
export const previewEmailTemplate = createAsyncThunk(
  'emailTemplates/previewEmailTemplate',
  async ({ id, variables = {} }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/email-templates/${id}/preview`, { variables });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * EmailTemplate Slice
 */
const emailTemplateSlice = createSlice({
  name: 'emailTemplates',
  initialState: {
    templates: [],
    currentTemplate: null,
    preview: null,
    loading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      pages: 0
    }
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentTemplate: (state) => {
      state.currentTemplate = null;
    },
    clearPreview: (state) => {
      state.preview = null;
    }
  },
  extraReducers: (builder) => {
    // getEmailTemplates
    builder
      .addCase(getEmailTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEmailTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload.templates;
        state.pagination = action.payload.pagination;
      })
      .addCase(getEmailTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Email şablonları yüklenemedi';
      });

    // getEmailTemplateById
    builder
      .addCase(getEmailTemplateById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEmailTemplateById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTemplate = action.payload;
      })
      .addCase(getEmailTemplateById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Email şablonu yüklenemedi';
      });

    // createEmailTemplate
    builder
      .addCase(createEmailTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEmailTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.templates.unshift(action.payload);
      })
      .addCase(createEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Email şablonu oluşturulamadı';
      });

    // updateEmailTemplate
    builder
      .addCase(updateEmailTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEmailTemplate.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.templates.findIndex(
          template => template._id === action.payload._id
        );
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
        if (state.currentTemplate && state.currentTemplate._id === action.payload._id) {
          state.currentTemplate = action.payload;
        }
      })
      .addCase(updateEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Email şablonu güncellenemedi';
      });

    // deleteEmailTemplate
    builder
      .addCase(deleteEmailTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEmailTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = state.templates.filter(
          template => template._id !== action.payload
        );
        if (state.currentTemplate && state.currentTemplate._id === action.payload) {
          state.currentTemplate = null;
        }
      })
      .addCase(deleteEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Email şablonu silinemedi';
      });

    // previewEmailTemplate
    builder
      .addCase(previewEmailTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(previewEmailTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.preview = action.payload;
      })
      .addCase(previewEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Email şablonu önizlenemedi';
      });
  }
});

export const { clearError, clearCurrentTemplate, clearPreview } = emailTemplateSlice.actions;
export default emailTemplateSlice.reducer;

